import React, { useState } from 'react';
import { FadeIn } from './FadeIn';
import { Shield, Check, Star, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';
type Variant = 'primary' | 'final';
const PLANS = [{
  id: 'base',
  name: 'Kit Base',
  tagline: '6 tomos + 6 bonos',
  price: '$19.990',
  originalPrice: null as string | null,
  url: 'https://tumanualdigital.com/cart/50871577739300:1',
  features: ['6 tomos PDF (~40 pág c/u)', '6 bonos PDF (~30 pág c/u)', 'Descarga inmediata', 'Acceso de por vida']
}, {
  id: 'completo',
  name: 'Kit Completo',
  tagline: 'Kit Base + Pack Digital Fono',
  price: '$27.990',
  originalPrice: '$34.000',
  url: 'https://tumanualdigital.com/cart/50871604314148:1',
  featured: true,
  features: ['Todo el Kit Base (12 archivos)', 'Módulo 1: Tu Perfil Profesional Online', 'Módulo 2: Contenido que Convierte', 'Módulo 3: Red de Derivaciones', 'Kit Canva: 20 plantillas de Instagram']
}];
interface PricingSelectorProps {
  variant?: Variant;
  id?: string;
}
export function PricingSelector({
  variant = 'primary',
  id
}: PricingSelectorProps) {
  const [selectedId, setSelectedId] = useState('completo');
  const selected = PLANS.find(p => p.id === selectedId) ?? PLANS[1];
  const isFinal = variant === 'final';
  return <section id={id} className={`px-4 md:px-6 py-12 md:py-16 ${isFinal ? 'bg-brand-green' : 'bg-brand-cream'}`} data-id="element-80">
      <div className="max-w-3xl mx-auto" data-id="element-81">
        <FadeIn data-id="element-82">
          <div className="text-center mb-6 md:mb-8" data-id="element-83">
            {isFinal && <div className="inline-flex items-center gap-2 bg-brand-gold/20 text-brand-gold border border-brand-gold/40 text-[10px] md:text-[11px] font-semibold tracking-[0.14em] uppercase px-4 py-1.5 rounded-full mb-4" data-id="element-84">
                <Sparkles className="w-3.5 h-3.5" data-id="element-85" /> Última oportunidad en esta
                página
              </div>}
            <h2 className={`font-serif text-2xl md:text-[34px] font-bold leading-[1.2] mb-2 ${isFinal ? 'text-white' : 'text-brand-dark'}`} data-id="element-86">
              {isFinal ? <>
                  Elegí tu kit{' '}
                  <em className="italic text-brand-gold" data-id="element-87">antes de cerrar</em>
                </> : <>
                  Elegí tu kit y{' '}
                  <em className="italic text-brand-green" data-id="element-88">empezá hoy</em>
                </>}
            </h2>
            <p className={`text-[13px] md:text-[15px] ${isFinal ? 'text-white/70' : 'text-brand-muted'}`} data-id="element-89">
              Tocá la opción que querés y presioná comprar
            </p>
          </div>
        </FadeIn>

        <FadeIn delay={0.1} data-id="element-90">
          <div className={`rounded-2xl p-3.5 md:p-5 ${isFinal ? 'bg-white/5 border border-brand-gold/25' : 'bg-brand-white border border-brand-cream-dark shadow-[0_15px_40px_rgba(29,92,74,0.08)]'}`} data-id="element-91">
            {/* Selectable options */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4 mb-4 md:mb-5" data-id="element-92">
              {PLANS.map(plan => {
              const isSelected = plan.id === selectedId;
              const isPlanFeatured = plan.featured;
              const selectedRing = isPlanFeatured ? isFinal ? 'bg-brand-gold/15 border-brand-gold shadow-[0_0_22px_rgba(200,134,58,0.35)]' : 'bg-brand-green-soft border-brand-green shadow-[0_8px_22px_rgba(29,92,74,0.15)]' : isFinal ? 'bg-white/10 border-white/50' : 'bg-brand-cream border-brand-green/60';
              const idleRing = isFinal ? 'bg-white/[0.04] border-white/12 opacity-70 hover:opacity-100' : 'bg-brand-white border-brand-cream-dark opacity-80 hover:opacity-100';
              return <button key={plan.id} type="button" onClick={() => setSelectedId(plan.id)} aria-pressed={isSelected} className={`relative text-left rounded-xl p-4 md:p-5 border-2 transition-all duration-200 ${isSelected ? `${selectedRing} scale-[1.01]` : idleRing}`} data-id="element-93">
                    {isPlanFeatured && <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-brand-gold text-white text-[9px] md:text-[10px] font-bold tracking-[0.08em] uppercase px-3 py-0.5 rounded-full flex items-center gap-1 whitespace-nowrap shadow-md" data-id="element-94">
                        <Star className="w-2.5 h-2.5" fill="currentColor" data-id="element-95" /> Más
                        elegida
                      </div>}

                    {/* Radio indicator */}
                    <div className={`absolute top-3 right-3 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${isSelected ? isPlanFeatured ? 'bg-brand-gold border-brand-gold' : 'bg-brand-green border-brand-green' : isFinal ? 'bg-transparent border-white/40' : 'bg-brand-white border-brand-cream-dark'}`} data-id="element-96">
                      {isSelected && <Check className={`w-3 h-3 ${isPlanFeatured ? 'text-white' : 'text-white'}`} strokeWidth={4} data-id="element-97" />}
                    </div>

                    <div className="pr-7" data-id="element-98">
                      <h3 className={`text-[13px] md:text-[15px] font-bold leading-tight mb-0.5 ${isFinal ? 'text-white' : 'text-brand-dark'}`} data-id="element-99">
                        {plan.name}
                      </h3>
                      <p className={`text-[10px] md:text-[11px] font-medium mb-2.5 ${isFinal ? 'text-white/60' : 'text-brand-muted'}`} data-id="element-100">
                        {plan.tagline}
                      </p>

                      <div className="flex items-baseline gap-1.5 mb-2.5" data-id="element-101">
                        {plan.originalPrice && <span className={`line-through text-[11px] ${isFinal ? 'text-white/45' : 'text-brand-muted/60'}`} data-id="element-102">
                            {plan.originalPrice}
                          </span>}
                        <span className={`text-xl md:text-2xl font-bold leading-none ${isPlanFeatured ? 'text-brand-gold' : isFinal ? 'text-white' : 'text-brand-green'}`} data-id="element-103">
                          {plan.price}
                        </span>
                        <span className={`text-[9px] md:text-[10px] font-semibold ${isFinal ? 'text-white/55' : 'text-brand-muted'}`} data-id="element-104">
                          ARS
                        </span>
                      </div>

                      <ul className="space-y-1" data-id="element-105">
                        {plan.features.map((f, idx) => <li key={idx} className={`flex items-start gap-1.5 text-[10px] md:text-[11px] leading-snug ${isFinal ? 'text-white/80' : 'text-brand-dark'}`} data-id="element-106">
                            <Check className={`mt-[2px] shrink-0 w-3 h-3 ${isPlanFeatured ? 'text-brand-gold' : isFinal ? 'text-white/60' : 'text-brand-green'}`} strokeWidth={3} data-id="element-107" />
                            <span data-id="element-108">{f}</span>
                          </li>)}
                      </ul>
                    </div>
                  </button>;
            })}
            </div>

            {/* Shared CTA */}
            <motion.a key={selected.id} initial={{
            opacity: 0.7,
            y: 4
          }} animate={{
            opacity: 1,
            y: 0
          }} whileHover={{
            y: -1
          }} whileTap={{
            scale: 0.99
          }} href={selected.url} className={`block w-full text-center font-semibold text-[14px] md:text-base py-3.5 md:py-4 px-4 rounded-md transition-colors shadow-lg mb-3 ${selected.featured ? 'bg-brand-gold hover:bg-brand-gold-dark text-white' : 'bg-brand-green hover:bg-brand-green-hover text-white'}`} data-id="element-109">
              → SÍ, QUIERO {selected.name.toUpperCase()} · {selected.price} ARS
            </motion.a>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-1.5 sm:gap-3 text-[11px] md:text-[12px]" data-id="element-110">
              <span className={isFinal ? 'text-white/65' : 'text-brand-muted'} data-id="element-111">
                Pago único · Descarga inmediata · Sin suscripción
              </span>
              <span className={`flex items-center gap-1.5 font-semibold ${isFinal ? 'text-brand-gold' : 'text-brand-green'}`} data-id="element-112">
                <Shield className="w-3.5 h-3.5" data-id="element-113" /> Garantía 7 días
              </span>
            </div>
          </div>
        </FadeIn>
      </div>
    </section>;
}